<template>
  <div id="page-layout">
    <div id="page-header">
      <div class="page-title-layout">
        <router-link class="bc-link" :to="{name: isClient() ? 'clientList' : 'adminList'}">
          {{$t(`user.${pageMode}_list_title`)}}
        </router-link>
        <i class="fas fa-angle-right" v-if="editMode"></i>
        <router-link class="bc-link" :to="{name: isClient() ? 'clientManage' : 'adminManage'}" v-if="editMode">
          {{$t("header.title_profile")}}
        </router-link>
        <i class="fas fa-angle-right"></i>
        <p>{{$t(editMode ? "header.title_edit" : "header.title_create")}}</p>
      </div>
    </div>
    <form
      class="edit-form"
      novalidate
      autocomplete="off"
      @submit.prevent="handleSubmit"
      name="userEdit">

      <!--Workaround because FF auto-complete the first password field and the input text just before-->
      <input type="password" style="display: none">

      <c-input name="firstName" label="user.first_name" v-model.trim="user.firstName"
               inputClass="large-input" type="text" v-validate="'required'"/>

      <c-input name="lastName" label="user.last_name" v-model.trim="user.lastName"
               inputClass="large-input" type="text" v-validate="'required'"/>

      <c-input name="companyName" label="user.company" v-model.trim="user.companyName" v-if="isClient()"
               inputClass="large-input" type="text" v-validate="'required'"/>

      <!--We need fake name to avoid autocomplete on Chrome-->
      <c-input name="xxx" label="user.email" v-model.trim="user.email"
               inputClass="large-input" type="text" v-validate="'required|email'"/>

      <div class="field" v-if="isClient() && isSuperAdmin()">
        <label for="responsible">{{ $t("user.responsible") }}</label>
        <v-select id="responsible" v-model="selectedAdmin" :options="adminList"
                  v-bind:class="[{ 'is-invalid': errors.has('selectedAdmin')}, 'small-input']"
                  :searchable="true" v-validate="'required'" name="selectedAdmin" data-vv-as="selectedAdmin"/>
        <div v-if="errors.has('selectedAdmin')" class="invalid-feedback">{{ errors.first('selectedAdmin') }}</div>
      </div>

      <div class="field">
        <label for="language">{{ $t("user.language") }}</label>
        <v-select class="small-input" id="language" v-model="selectedLocale" :options="getLocales()"
                  :searchable="false"/>
      </div>

      <c-input name="passwordNew" label="user.password_new" v-model.trim="user.passwordNew"
               inputClass="small-input" type="password" v-validate="getPasswordValidation()"
               ref="passwordNew" data-vv-as="passwordNew" autocomplete="new-password"/>

      <c-input name="passwordNewConfirm" label="user.password_new_confirm" v-model.trim="user.passwordNewConfirm"
               inputClass="small-input" type="password" v-validate="getPasswordValidation()"
               ref="passwordNewConfirm" data-vv-as="passwordNewConfirm"/>

      <div class="actions">
        <input class="solid-button" type="submit" v-bind:value="$t('actions.save')"/>
        <input class="ghost-button" type="button" v-bind:value="$t('actions.cancel')" v-on:click="goBack()"/>
      </div>
    </form>
  </div>
</template>

<script>
  import Roles from "../models/Roles.js"
  import Admin from "../models/Admin.js"
  import Client from "../models/Client.js"
  import userService from "../services/userService.js"
  import Utils from "../services/utils.js"

  const PageModes = {
    CLIENT: 'client',
    ADMIN: 'admin'
  };

  export default {
    props: {
      pageMode: {
        type: String,
        default: function () {
          return PageModes.CLIENT
        }
      },
    },
    data() {
      return {
        user: this.isClient() ? new Client() : new Admin(),
        selectedLocale: null,
        selectedAdmin: null,
        adminList: [],
        editMode: false
      };
    },
    watch: {
      '$route'(to, from) {
        if (to.params.pageMode !== this.pageMode) {
          this.loadUser();
        }
      },
      selectedLocale(localeObj) {
        this.user.locale = localeObj.value;
      },
      selectedAdmin(adminObj) {
        if (adminObj) {
          this.user.responsibleId = adminObj.value;
        }
      }
    },
    methods: {
      isClient() {
        return this.pageMode === PageModes.CLIENT;
      },
      isSuperAdmin() {
        return userService.getMyRole() === Roles.superAdmin;
      },
      getLocales() {
        return [
          {label: this.$t("languages.fr"), value: "fr"},
          {label: this.$t("languages.en"), value: "en"}];
      },
      getPasswordValidation() {
        if (this.editMode) {
          return 'password';
        } else {
          return 'required|password'
        }
      },
      handleSubmit() {
        this.$validator.validate().then(valid => {

          if (!Utils.isBlank(this.user.passwordNew) && this.user.passwordNew !== this.user.passwordNewConfirm) {
            this.errors.add({
              field: 'passwordNew',
              msg: this.$t('messages.pwd_confirm')
            });
            this.errors.add({
              field: 'passwordNewConfirm',
              msg: this.$t('messages.pwd_confirm')
            });
            valid = false
          }

          if (valid) {
            const displayName = this.user.displayName;
            const mode = this.editMode ? 'update' : 'create';

            const call = this.editMode ?
              (this.isClient() ? userService.updateClient : userService.updateAdmin)
              : (this.isClient() ? userService.createClient : userService.createAdmin);

            call(this.user).then((response) => {
              this.$store.dispatch('alert/success', this.$t(`alerts.${mode}_success`, {name: displayName}));
              this.goBack();
            }).catch((error) => {
              this.$store.dispatch('alert/error', {
                message: this.$t(`alerts.${mode}_error`, {name: displayName}),
                error: error
              });
            });
          }
        });
      },
      goBack() {
        const routeName = this.isClient() ? 'clientList' : 'adminList';
        this.$router.push({name: routeName})
      },
      selectLocale(locale) { // v-select need an object...
        this.selectedLocale = this.getLocales().find(function (e) {
          return e.value === locale;
        });
      },
      selectAdmin() {
        if (this.user && this.user.responsibleId) {
          this.selectedAdmin = this.adminList.find(e => {
            return e.value === this.user.responsibleId;
          });
        }
      },
      loadAdminsSelect() {
        if (this.isSuperAdmin()) {
          userService.listAdmins().then(list => {
            this.adminList = list.map(admin => {
              return {label: admin.displayName, value: admin.id}
            });
            this.selectAdmin();
          })
        }
      },
      loadUser() {
        const userId = this.$route.params.id;
        if (userId > 0) { // edit
          this.editMode = true;
          const call = this.isClient() ? userService.getClient : userService.getAdmin;
          call(userId).then(user => {
            this.user = user;
            this.selectLocale(user.locale);
            this.selectAdmin();
          });
        } else { // create
          this.editMode = false;
          this.user = this.isClient() ? new Client() : new Admin();
          this.selectLocale(this.$i18n.locale);
        }
        this.loadAdminsSelect();
      }
    },
    mounted() {
      this.loadUser();
    }
  }
</script>

<style lang="scss" scoped>
  @import '../style/form.scss';

  #page-layout {
    display: grid;
    grid-template-columns: 20% [content] auto 20%;
  }

  #page-header {
    @extend %common-header;
  }
</style>



// WEBPACK FOOTER //
// src/pages/editUser.vue